import React, { useCallback } from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { translate } from '../../i18n'
import { SettingsScreenLayout, SettingsLoadingView } from '@/components/layouts/SettingsScreenLayout'
import { SettingsCard } from '@/components/layouts/SettingsCard'

const AVATAR_COLORS = ['Shades/Maroon 1', 'Brand/Green', 'Brand/Purple'] as const

export function LoungeScheduledGamesScreen() {
  const { loungeId: loungeIdStr } = useLocalSearchParams<{ loungeId: string }>()
  const loungeId = loungeIdStr as Id<'lounges'> | undefined
  const router = useRouter()

  const scheduledGames = useQuery(api.lounges.queries.getRecurringLoungeSchedules, loungeId ? { loungeId } : 'skip')

  const handleCreateGame = useCallback(() => {
    if (!loungeId) return
    router.push({
      pathname: '/lounges/[loungeId]/game-settings',
      params: { loungeId: loungeId as string }
    } as any)
  }, [loungeId, router])

  const handleEditSchedule = useCallback(
    (scheduleId: string) => {
      if (!loungeId) return
      router.push({
        pathname: '/lounges/[loungeId]/game-settings',
        params: { loungeId: loungeId as string, scheduleId }
      } as any)
    },
    [loungeId, router]
  )

  return (
    <SettingsScreenLayout title={translate('loungeScheduledGamesScreen.title')} onBack={() => router.back()}>
      {!scheduledGames ? (
        <SettingsLoadingView />
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          <Pressable
            onPress={handleCreateGame}
            accessibilityRole="button"
            accessibilityLabel={translate('loungeScheduledGamesScreen.createGame')}
            style={({ pressed }) => [styles.createGameButton, pressed && { opacity: 0.85 }]}
          >
            <View style={styles.plusIcon}>
              <DS.Icon name="Plus--small" size={32} color="Global/White" />
            </View>
            <DS.Text variant="Heading 4/Medium" style={styles.createGameLabel}>
              {translate('loungeScheduledGamesScreen.createGame')}
            </DS.Text>
            <DS.Icon name="Arrow---right" size={20} color="Global/Black" />
          </Pressable>

          <SettingsCard gap={16}>
            {scheduledGames.length === 0 ? (
              <DS.Text variant="Body Small/Medium" style={styles.emptyText}>
                {translate('loungeScheduledGamesScreen.empty')}
              </DS.Text>
            ) : (
              scheduledGames.map((schedule, index) => (
                <Pressable
                  key={schedule._id}
                  onPress={() => handleEditSchedule(schedule._id)}
                  style={styles.gameRow}
                  accessibilityRole="button"
                  accessibilityLabel={schedule.name}
                >
                  <View
                    style={[
                      styles.gameAvatar,
                      { backgroundColor: DS.palette[AVATAR_COLORS[index % AVATAR_COLORS.length]] }
                    ]}
                  />
                  <DS.Text variant="Body Small/Medium" style={styles.gameName} numberOfLines={1}>
                    {schedule.name}
                  </DS.Text>
                  <DS.Icon name="Arrow---right" size={20} color="Global/Black" />
                </Pressable>
              ))
            )}
          </SettingsCard>
        </ScrollView>
      )}
    </SettingsScreenLayout>
  )
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 24
  },
  createGameButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  plusIcon: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: DS.palette['Brand/Purple'],
    alignItems: 'center',
    justifyContent: 'center'
  },
  createGameLabel: {
    flex: 1
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
  },
  gameAvatar: {
    width: 36,
    height: 36,
    borderRadius: 999
  },
  gameName: {
    flex: 1
  },
  emptyText: {
    color: DS.palette['Brand/Dark Grey']
  }
})
